/**
 * Der Ablauf vom Klick auf ein Projekt bis zum fertigen Belegentwurf.
 *
 * WARUM diese Datei: der Anlagedialog trug links eine Spalte mit sechs
 * Schaltern, die VOR der Wahl des Projekts eingestellt werden mussten. Die
 * meisten davon konnten die Frage gar nicht beantworten, die sie stellten —
 * die Abrechnungsart folgt dem Projekt, die Abschlagshoehe braucht dessen
 * Auftragswert, der Abschlagsabzug dessen Abschlaege. Gefragt wird deshalb
 * NACH dem Klick, und nur das, was fuer dieses eine Projekt offen ist.
 *
 * Vor der Wahl bleibt nur, was die LISTE steuert: die Belegart (welche
 * Projekte erscheinen) und der Leistungszeitraum (welche Mengen und damit
 * welcher Betrag je Zeile stehen).
 *
 * Damit stehen bis zu fuenf Rueckfragen hintereinander. Sie duerfen nicht als
 * fuenf voneinander unabhaengige Fenster aufpoppen: der Ablauf ist EINE
 * Entscheidungskette, und man muss sehen, wo man steht und zurueckkommen. Die
 * Kette selbst — welche Schritte fuer dieses Projekt gelten, welcher als
 * naechstes dran ist, welcher der vorige war — steht hier als pure Funktion
 * (stehende Entscheidung „duenner Container, Logik in puren Funktionen"; das
 * Projekt hat keine DOM-Testumgebung, Komponenten sind nicht testbar).
 *
 * Geschrieben wird waehrend der Kette NICHTS. Erst der letzte Schritt legt den
 * Beleg an; Abbrechen an jeder Stelle laesst nichts zurueck. Die einzige
 * Ausnahme ist die Kundenzuweisung, und genau deshalb steht sie am ENDE —
 * nach ihr folgt kein Schritt mehr, in dem noch abgebrochen werden koennte.
 */

import type { DocumentType } from '../types';
import {
    asksBillingMode,
    modeFromStance,
    type BillingMode,
    type BillingStance,
} from './billingMode';
import { centsFromEuroInput, roundCents, type Cents } from './money';

/**
 * Die Schritte in ihrer festen Reihenfolge.
 *
 * Die Reihenfolge ist nicht beliebig: 'billing-mode' steht vorn, weil
 * 'deduct-advances' und 'prior-months' von der Antwort abhaengen (ein
 * kumulativer Beleg zieht ohnehin alle Vorbelege ab und uebergeht keine
 * Vormonate). 'customer' steht hinten, weil es der einzige Schritt ist, der
 * schreibt.
 */
export const PICKER_STEP_ORDER = [
    'billing-mode',
    'advance-amount',
    'deduct-advances',
    'prior-months',
    'customer',
] as const;

export type PickerStepId = (typeof PICKER_STEP_ORDER)[number];

/** Die Eingaben der Abschlagshoehe, so wie sie im Feld stehen. */
export interface AdvanceAmountChoice {
    kind: 'percent' | 'fixed';
    /** Rohtext des Prozentfelds. */
    percent: string;
    /** Rohtext des Betragsfelds (netto, EUR). */
    fixed: string;
}

/** Das geprueft Eingegebene — genau die Felder, die createAdvanceInvoice braucht. */
export interface AdvanceAmountResult {
    percent?: number;
    fixedNetCents?: Cents;
}

/**
 * Was ueber das Projekt bekannt ist, wenn die Kette geplant wird.
 *
 * Bewusst nur Zahlen und Wahrheitswerte: die Kette darf nicht davon abhaengen,
 * WIE die Belege geladen wurden, sonst laesst sie sich nicht pruefen.
 */
export interface PickerStepFacts {
    docType: DocumentType;
    /** Der Zustand des Projekts (billingMode.ts) — entscheidet ueber Schritt 1. */
    stance: BillingStance;
    /**
     * Anzahl der abzugsfaehigen Abschlaege des Projekts (isDeductibleAdvance).
     * 0 heisst: nicht fragen. Eine Frage, deren beide Antworten dasselbe
     * bewirken, ist keine Frage — sie ist ein Klick.
     */
    deductibleAdvances: number;
    /**
     * Offene Monate VOR dem gewaehlten Zeitraum. 0, wenn kein Zeitraum gesetzt
     * ist oder nichts davor liegt.
     */
    priorOpenMonths: number;
    /** Ist am Projekt ein Rechnungsempfaenger hinterlegt? */
    hasCustomer: boolean;
}

/** Die bisher gegebenen Antworten. */
export interface PickerAnswers {
    /** Schritt 1. `null` = noch offen. */
    mode: BillingMode | null;
    /** Schritt 2. `null` = noch offen. */
    advance: AdvanceAmountResult | null;
    /** Schritt 3. `null` = noch offen. */
    deductAdvances: boolean | null;
    /**
     * Schritt 4. `undefined` = noch offen, `null` = bewusst ohne Vormonate,
     * sonst der vorgezogene Beginn ('JJJJ-MM-TT').
     *
     * Drei Zustaende und nicht zwei: „nicht gefragt" und „gefragt, abgelehnt"
     * sind verschieden — sonst stellte die Kette die Frage nach jedem Zurueck
     * erneut.
     */
    overrideFrom: string | null | undefined;
}

export const EMPTY_PICKER_ANSWERS: PickerAnswers = {
    mode: null,
    advance: null,
    deductAdvances: null,
    overrideFrom: undefined,
};

/**
 * Das Modell, mit dem die Kette rechnet, solange Schritt 1 offen ist.
 *
 * Bewusst fortschreibend: das ist die GROESSERE Kette. Kumulativ entfallen
 * 'deduct-advances' und 'prior-months', fortschreibend koennen beide kommen.
 * Die Schrittzahl kann nach der Antwort also nur schrumpfen, nie wachsen — und
 * eine Anzeige, die von „Schritt 1 von 3" auf „von 2" faellt, ist ehrlicher
 * als eine, die waehrend des Ausfuellens neue Schritte nachschiebt.
 */
const assumedMode = (facts: PickerStepFacts, answers: PickerAnswers): BillingMode =>
    answers.mode ?? modeFromStance(facts.stance, 'PROGRESSIVE');

/**
 * Alle Schritte, die fuer dieses Projekt gelten — beantwortete eingeschlossen.
 *
 * Das ist der Pfad, den die Schrittanzeige zeigt, nicht die Restliste.
 */
export const pickerStepPlan = (
    facts: PickerStepFacts,
    answers: PickerAnswers = EMPTY_PICKER_ANSWERS,
): PickerStepId[] => {
    const steps: PickerStepId[] = [];
    const mode = assumedMode(facts, answers);

    // 1. Abrechnungsart — nur bei Zustand 'open' und nur bei Belegarten, die
    //    das Modell ueberhaupt lesen (billingMode.ts).
    if (asksBillingMode(facts.stance, facts.docType)) steps.push('billing-mode');

    // 2. Abschlagshoehe — der Abschlag hat keine LV-Positionen, seine Hoehe IST
    //    der Beleg.
    if (facts.docType === 'ADVANCE_INVOICE') steps.push('advance-amount');

    // 3. Abschlagsabzug — nur bei der gewoehnlichen Rechnung: Schluss- und
    //    Restrechnung ziehen immer ab, der Abschlag zieht nichts ab, und ein
    //    kumulativer Beleg zieht ohnehin ALLE Vorbelege ab. Und nur, wenn es
    //    ueberhaupt etwas abzuziehen gibt.
    if (
        facts.docType === 'INVOICE' &&
        mode === 'PROGRESSIVE' &&
        facts.deductibleAdvances > 0
    ) {
        steps.push('deduct-advances');
    }

    // 4. Uebergangene Vormonate — dieselbe Bedingung wie bisher im Dialog: der
    //    Abschlag kennt keine offenen Positionen, der kumulative Beleg zeigt
    //    ohnehin den Gesamtstand.
    if (
        facts.docType !== 'ADVANCE_INVOICE' &&
        !(facts.docType === 'INVOICE' && mode === 'CUMULATIVE') &&
        facts.priorOpenMonths > 0
    ) {
        steps.push('prior-months');
    }

    // 5. Kunde — ohne Rechnungsempfaenger entsteht kein Beleg. Als letzter
    //    Schritt, weil er als einziger schreibt.
    if (!facts.hasCustomer) steps.push('customer');

    return steps;
};

/** Ist dieser Schritt beantwortet? */
const isAnswered = (step: PickerStepId, answers: PickerAnswers): boolean => {
    switch (step) {
        case 'billing-mode':
            return answers.mode != null;
        case 'advance-amount':
            return answers.advance != null;
        case 'deduct-advances':
            return answers.deductAdvances != null;
        case 'prior-months':
            return answers.overrideFrom !== undefined;
        case 'customer':
            // Der Kundenschritt hat keine Antwort — er hat eine Wirkung. Steht
            // er noch im Plan, ist am Projekt kein Kunde hinterlegt; nach der
            // Zuweisung faellt er aus dem Plan heraus.
            return false;
    }
};

/** Der Schritt, der als naechstes dran ist — `null`, wenn die Kette durch ist. */
export const nextPickerStep = (
    facts: PickerStepFacts,
    answers: PickerAnswers,
): PickerStepId | null =>
    pickerStepPlan(facts, answers).find((step) => !isAnswered(step, answers)) ?? null;

/**
 * Der Schritt hinter dem „Zurück" — `null`, wenn der aktuelle der erste ist.
 *
 * Er wird aus dem Plan gelesen und nicht mitgeschrieben: eine eigene Historie
 * liefe nach jedem Zurueck-und-anders-Antworten aus dem Tritt, denn die Kette
 * selbst haengt an den Antworten (kumulativ laesst Schritte wegfallen).
 */
export const previousPickerStep = (
    facts: PickerStepFacts,
    answers: PickerAnswers,
): PickerStepId | null => {
    const plan = pickerStepPlan(facts, answers);
    const current = plan.findIndex((step) => !isAnswered(step, answers));
    const upto = current < 0 ? plan.length : current;
    for (let i = upto - 1; i >= 0; i -= 1) {
        if (isAnswered(plan[i], answers)) return plan[i];
    }
    return null;
};

/**
 * Die Antwort eines Schritts zuruecknehmen — der Weg zurueck.
 *
 * Bewusst nur die EINE Antwort: die spaeteren Schritte behalten ihre. Wer
 * zurueckgeht, seine Wahl bestaetigt und wieder vorwaerts geht, soll nicht
 * alles erneut beantworten. Faellt ein spaeterer Schritt durch die neue
 * Antwort aus dem Plan (kumulativ statt fortschreibend), bleibt seine Antwort
 * folgenlos stehen — sie wird nirgends gelesen, weil der Plan sie nicht mehr
 * fuehrt.
 */
export const clearPickerAnswer = (
    step: PickerStepId,
    answers: PickerAnswers,
): PickerAnswers => {
    switch (step) {
        case 'billing-mode':
            return { ...answers, mode: null };
        case 'advance-amount':
            return { ...answers, advance: null };
        case 'deduct-advances':
            return { ...answers, deductAdvances: null };
        case 'prior-months':
            return { ...answers, overrideFrom: undefined };
        case 'customer':
            // Eine Zuweisung laesst sich hier nicht zuruecknehmen — sie steht
            // am Projekt. Der Schritt hat deshalb kein Zurueck.
            return answers;
    }
};

/**
 * Die Antworten, bereinigt um alles, was der Plan nicht (mehr) fuehrt.
 *
 * WARUM: „zurueck" nimmt bewusst nur EINE Antwort zurueck, damit niemand nach
 * einer Korrektur alles erneut beantworten muss. Damit kann eine Antwort stehen
 * bleiben, deren Schritt durch die Korrektur aus dem Plan gefallen ist — wer
 * Vormonate einbezieht, zurueckgeht und dann „kumulativ" waehlt, hat einen
 * vorgezogenen Beginn beantwortet, den es in dieser Kette nicht mehr gibt. Der
 * Beleg wies ihn sonst als Leistungszeitraum aus, obwohl die Frage danach
 * ueberhaupt nicht mehr gestellt wurde.
 *
 * Bereinigt wird beim LESEN und nicht beim Zuruecknehmen: so bleibt die Antwort
 * erhalten, falls der Schritt durch ein weiteres Zurueck wieder in den Plan
 * kommt, und trotzdem wirkt ausschliesslich, was auch gefragt wurde.
 */
export const effectivePickerAnswers = (
    facts: PickerStepFacts,
    answers: PickerAnswers,
): PickerAnswers => {
    const plan = new Set(pickerStepPlan(facts, answers));
    let out = answers;
    for (const step of PICKER_STEP_ORDER) {
        if (!plan.has(step)) out = clearPickerAnswer(step, out);
    }
    return out;
};

/**
 * Wo im Ablauf man steht: „Schritt {position} von {total}".
 *
 * `position` ist 1-basiert. Ein Schritt, der nicht im Plan steht, ergibt 0 —
 * die Anzeige entfaellt dann, statt eine Zahl zu erfinden.
 */
export const pickerStepPosition = (
    facts: PickerStepFacts,
    answers: PickerAnswers,
    step: PickerStepId,
): { position: number; total: number } => {
    const plan = pickerStepPlan(facts, answers);
    return { position: plan.indexOf(step) + 1, total: plan.length };
};

/**
 * Die Abschlagshoehe aus den Rohtexten — dieselbe Pruefung, die frueher
 * mitten im Anlegen stand.
 *
 * Sie gehoert vor den Beleg und nicht hinter ihn: bisher fiel ein leeres oder
 * unsinniges Feld erst auf, nachdem der Nutzer auf ein Projekt geklickt hatte,
 * und die Meldung erschien im Dialog hinter der Liste.
 *
 * @returns entweder das Ergebnis oder den Grund, warum es keines gibt. Nie
 *          beides, und nie keines von beidem.
 */
export const readAdvanceAmount = (
    choice: AdvanceAmountChoice,
): { result: AdvanceAmountResult; error: null } | { result: null; error: string } => {
    if (choice.kind === 'fixed') {
        const cents = centsFromEuroInput(choice.fixed);
        if (cents == null || cents <= 0) {
            return { result: null, error: 'Bitte einen Abschlagsbetrag größer als 0,00 € eingeben.' };
        }
        return { result: { fixedNetCents: cents }, error: null };
    }
    const percent = Number(choice.percent.replace(',', '.'));
    if (!Number.isFinite(percent) || percent <= 0) {
        return { result: null, error: 'Bitte einen Prozentsatz größer als 0 eingeben.' };
    }
    return { result: { percent }, error: null };
};

/**
 * Was der Abschlag tatsaechlich fordert — und ob das mehr ist als der ganze
 * Auftrag wert.
 *
 * WARUM diese Pruefung: `readAdvanceAmount` prueft nur „groesser als null", und
 * das Anlegen prueft nur, ob es ueberhaupt eine Bemessungsgrundlage gibt. Ein
 * Festbetrag von 25.000,00 auf einem Auftrag ueber 8.000,00 lief bis hierhin
 * ohne jeden Widerspruch durch — der Beleg forderte das Dreifache des
 * Auftragswerts, und auffallen konnte es erst beim Kunden.
 *
 * Bewusst KEINE Sperre, sondern ein Befund, den die Oberflaeche
 * bestaetigen laesst: der Fall ist ungewoehnlich, aber nicht immer falsch. Ein
 * Leistungsverzeichnis ohne die schon vereinbarten Nachtraege traegt einen zu
 * niedrigen Auftragswert, und der vereinbarte Abschlag liegt dann rechtmaessig
 * darueber. Eine harte Sperre haette dort keinen Ausweg; eine blosse Warnung
 * klickt man weg, ohne sie gelesen zu haben.
 *
 * Der Betrag wird hier auch fuer die Vorschau gerechnet — dieselbe Zahl, die
 * der Beleg spaeter fordert, aus derselben Formel. Zwei Rechnungen desselben
 * Betrags liefen sonst genau dann auseinander, wenn es darauf ankommt.
 */
export interface AdvanceAmountCheck {
    /** Was der Abschlag netto fordert. */
    amountNetCents: Cents;
    /** Liegt er ueber der Bemessungsgrundlage? */
    exceedsBasis: boolean;
}

export const checkAdvanceAmount = (
    result: AdvanceAmountResult,
    basisNetCents: Cents,
): AdvanceAmountCheck => {
    // Dieselbe Formel wie in buildAdvanceLine: Prozent auf die
    // Bemessungsgrundlage, sonst der eingegebene Festbetrag.
    const amountNetCents =
        result.fixedNetCents != null
            ? result.fixedNetCents
            : roundCents((basisNetCents * (result.percent ?? 0)) / 100);
    return {
        amountNetCents,
        // Ohne Bemessungsgrundlage gibt es nichts zu ueberschreiten — dass sie
        // fehlt, meldet der Schritt bereits als eigenen Fehler.
        exceedsBasis: basisNetCents > 0 && amountNetCents > basisNetCents,
    };
};
